import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { ArrowLeft, Pencil, Archive, ArchiveRestore, Trash2, PackagePlus, PackageMinus, Lock, Truck, CheckCircle2 as CheckCircle2Icon, ClipboardList, Users, Circle, Plus } from "lucide-react";
import { statusMeta, formatDate, roleLabel } from "../lib/format";
import DeleteProjectDialog from "../components/DeleteProjectDialog";
import ProjectFormDialog from "../components/ProjectFormDialog";
import TransferInventoryDialog from "../components/TransferInventoryDialog";
import ReturnInventoryDialog from "../components/ReturnInventoryDialog";
import SiteDeliveryDialog from "../components/SiteDeliveryDialog";
import ReceiveDeliveryDialog from "../components/ReceiveDeliveryDialog";
import NewExpectedDeliveryDialog from "../components/NewExpectedDeliveryDialog";
import TaskFormDialog from "../components/TaskFormDialog";

export default function ProjectDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [receiveDeliveryOpen, setReceiveDeliveryOpen] = useState(false);
  const [newExpectedOpen, setNewExpectedOpen] = useState(false);
  const [expected, setExpected] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const { canDelete } = useAuth();

  const load = async () => {
    const [{ data: p }, { data: tx }, { data: dv }, { data: exp }, { data: tk }] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/inventory/transactions`, { params: { project_id: id } }),
      api.get(`/projects/${id}/deliveries`),
      api.get(`/expected-deliveries`, { params: { project_id: id } }),
      api.get(`/tasks`, { params: { project_id: id } }),
    ]);
    setProject(p);
    setTransactions(tx);
    setDeliveries(dv);
    setExpected(exp);
    setTasks(tk);
  };

  useEffect(() => { load(); }, [id]);

  if (!project) {
    return <div className="p-4 md:p-8 text-slate-500 font-mono text-sm">Loading project…</div>;
  }

  const meta = statusMeta(project.status, project.archived);

  const submitEdit = async (data) => {
    const patch = { name: data.name, client: data.client, location: data.location, status: data.status, start_date: data.start_date || null, end_date: data.end_date || null, notes: data.notes, assignees: data.assignees || [], crew_size: Number(data.crew_size) || 0 };
    await api.patch(`/projects/${id}`, patch);
    toast.success("Project updated");
    load();
  };

  const doArchive = async () => {
    await api.post(`/projects/${id}/${project.archived ? "restore" : "archive"}`);
    toast.success(project.archived ? "Restored" : "Archived");
    load();
  };

  const doDelete = async () => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success(`Deleted "${project.name}"`);
      navigate("/projects");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      throw e;
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={project.archived ? "Archived Project" : "Project"}
        title={project.name}
        description={`${project.client || "No client"}  ·  ${project.location || "No location"}`}
        testId="project-details-header"
        actions={
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn" className="rounded-sm">
            <ArrowLeft size={16} className="mr-1.5" /> Back
          </Button>
        }
      />

      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Overview</div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-semibold ${meta.cls}`}>
                {meta.label}
              </span>
            </div>
            <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-widest font-semibold text-slate-500">Client</dt>
                <dd className="mt-1 text-slate-900">{project.client || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest font-semibold text-slate-500">Location</dt>
                <dd className="mt-1 text-slate-900">{project.location || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest font-semibold text-slate-500">Created</dt>
                <dd className="mt-1 font-mono text-slate-700">{formatDate(project.created_at)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest font-semibold text-slate-500">Updated</dt>
                <dd className="mt-1 font-mono text-slate-700">{formatDate(project.updated_at)}</dd>
              </div>
              {project.notes && (
                <div className="md:col-span-2">
                  <dt className="text-xs uppercase tracking-widest font-semibold text-slate-500">Notes</dt>
                  <dd className="mt-1 text-slate-700 whitespace-pre-wrap">{project.notes}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">On-site Inventory</div>
                <div className="mt-1 font-display text-xl font-bold text-slate-900">
                  Net items transferred to project
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  className="rounded-sm bg-blue-800 hover:bg-blue-900 text-white"
                  onClick={() => setReceiveDeliveryOpen(true)}
                  data-testid="receive-delivery-btn"
                >
                  <Truck size={14} className="mr-1.5" /> Receive Delivery
                </Button>
                <Button
                  variant="outline"
                  className="rounded-sm border-slate-300"
                  onClick={() => setTransferOpen(true)}
                  data-testid="transfer-inventory-btn"
                >
                  <PackagePlus size={14} className="mr-1.5" /> Transfer In
                </Button>
                <Button
                  variant="outline"
                  className="rounded-sm border-slate-300"
                  onClick={() => setReturnOpen(true)}
                  data-testid="return-inventory-btn"
                >
                  <PackageMinus size={14} className="mr-1.5" /> Return to Warehouse
                </Button>
              </div>
            </div>
            <div>
              {project.onsite_inventory?.length ? (
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                      <th className="p-4">Item</th>
                      <th className="p-4">SKU</th>
                      <th className="p-4 text-right">Qty on-site</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.onsite_inventory.map((i) => (
                      <tr key={i._id} className="border-b border-slate-200 last:border-b-0">
                        <td className="p-4 text-slate-900">{i.item_name}</td>
                        <td className="p-4 font-mono text-xs text-slate-500">{i.sku || "—"}</td>
                        <td className="p-4 text-right font-mono font-semibold text-slate-900">{i.net}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">
                  No inventory currently on-site.
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">History</div>
              <div className="mt-1 font-display text-xl font-bold text-slate-900">Transaction log</div>
            </div>
            {transactions.length ? (
              <table className="w-full text-sm min-w-[720px]" data-testid="project-transactions">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    <th className="p-4">Date</th>
                    <th className="p-4">Type</th>
                    <th className="p-4">Item</th>
                    <th className="p-4 text-right">Qty</th>
                    <th className="p-4">By</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => {
                    const badge =
                      t.type === "transfer" ? "bg-blue-100 text-blue-800" :
                      t.type === "return" ? "bg-emerald-100 text-emerald-800" :
                      t.type === "site_delivery" ? "bg-indigo-100 text-indigo-800" :
                      t.type === "adjustment" ? "bg-amber-100 text-amber-900" :
                      "bg-slate-100 text-slate-700";
                    const label = t.type === "site_delivery" ? "site delivery" : t.type;
                    return (
                    <tr key={t.id} className="border-b border-slate-200 last:border-b-0">
                      <td className="p-4 font-mono text-xs text-slate-500">{formatDate(t.created_at)}</td>
                      <td className="p-4">
                        <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-sm ${badge}`}>
                          {label}
                        </span>
                      </td>
                      <td className="p-4 text-slate-900">{t.item_name} <span className="font-mono text-xs text-slate-500">({t.item_sku})</span></td>
                      <td className="p-4 text-right font-mono font-semibold">{t.quantity}</td>
                      <td className="p-4 text-slate-700">{t.user_name}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            ) : (
              <div className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">
                No transactions logged for this project yet.
              </div>
            )}
          </div>

          {/* Tasks: per-day assignments */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Assignments</div>
                <div className="mt-1 font-display text-xl font-bold text-slate-900">
                  <ClipboardList size={16} className="inline -mt-1 mr-1" /> Tasks
                </div>
                <p className="mt-1 text-sm text-slate-600">Give each teammate a clear task for a specific day. Shows up on the Calendar.</p>
              </div>
              <Button
                onClick={() => { setEditingTask(null); setTaskDialogOpen(true); }}
                className="rounded-sm bg-blue-800 hover:bg-blue-900 text-white"
                data-testid="add-task-btn"
              >
                <Plus size={14} className="mr-1.5" /> Add Task
              </Button>
            </div>
            {tasks.length ? (
              <table className="w-full text-sm min-w-[720px]" data-testid="project-tasks">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="p-3 w-8"></th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Task</th>
                    <th className="p-3">Assignee</th>
                    <th className="p-3 text-right">Qty</th>
                    <th className="p-3 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t) => {
                    const done = t.status === "done";
                    return (
                      <tr key={t.id} className="border-b border-slate-200 last:border-b-0" data-testid={`task-row-${t.id}`}>
                        <td className="p-3">
                          <button
                            onClick={async () => { await api.patch(`/tasks/${t.id}`, { status: done ? "pending" : "done" }); load(); }}
                            aria-label={done ? "Mark pending" : "Mark done"}
                          >
                            {done ? <CheckCircle2Icon size={16} className="text-emerald-600" /> : <Circle size={16} className="text-slate-400" />}
                          </button>
                        </td>
                        <td className="p-3 font-mono text-xs text-slate-500">{t.date}</td>
                        <td className="p-3">
                          <div className={`font-semibold ${done ? "line-through text-slate-500" : "text-slate-900"}`}>{t.title}</div>
                          {t.location && <div className="text-xs text-slate-600">{t.location}</div>}
                        </td>
                        <td className="p-3 text-slate-700">{t.assignee_name || <span className="text-slate-400">Unassigned</span>}</td>
                        <td className="p-3 text-right font-mono">{t.quantity != null ? t.quantity : "—"}</td>
                        <td className="p-3 text-right">
                          <button onClick={() => { setEditingTask(t); setTaskDialogOpen(true); }} className="text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-blue-800 mr-3">Edit</button>
                          <button
                            onClick={async () => { if (!window.confirm("Delete task?")) return; await api.delete(`/tasks/${t.id}`); toast.success("Task deleted"); load(); }}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`task-delete-${t.id}`}
                            aria-label="Delete task"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">No tasks yet. Add one to schedule work for a teammate.</div>
            )}
          </div>

          {/* Expected deliveries for this project */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Scheduled</div>
                <div className="mt-1 font-display text-xl font-bold text-slate-900">Expected Deliveries</div>
                <p className="mt-1 text-sm text-slate-600">Pre-loaded lines from the office. Use "Receive Delivery" on-site to check them off.</p>
              </div>
              <Button
                onClick={() => setNewExpectedOpen(true)}
                className="rounded-sm bg-blue-800 hover:bg-blue-900 text-white"
                data-testid="new-expected-project-btn"
              >
                <Truck size={14} className="mr-1.5" /> Schedule New
              </Button>
            </div>
            {expected.length ? (
              <div className="divide-y divide-slate-200" data-testid="project-expected">
                {expected.map((d) => {
                  const statusCls =
                    d.status === "complete" ? "bg-emerald-100 text-emerald-800" :
                    d.status === "partial" ? "bg-blue-100 text-blue-800" :
                    "bg-slate-100 text-slate-700";
                  return (
                  <div key={d.id} className="p-6">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${statusCls}`}>{d.status}</span>
                          <span className="font-mono text-sm font-semibold text-slate-900">{d.reference || "no reference"}</span>
                        </div>
                        {d.note && <div className="text-xs text-slate-500 mt-1">{d.note}</div>}
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-xs">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Expected</div>
                          <div className="font-mono font-semibold text-slate-900">{d.totals.expected}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Received</div>
                          <div className="font-mono font-semibold text-slate-900">{d.totals.received}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Remaining</div>
                          <div className={`font-mono font-semibold ${d.totals.remaining ? "text-amber-800" : "text-slate-900"}`}>{d.totals.remaining}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Over</div>
                          <div className={`font-mono font-semibold ${d.totals.over ? "text-amber-800" : "text-slate-900"}`}>{d.totals.over}</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 border border-slate-200 rounded-sm overflow-hidden">
                      <table className="w-full text-sm min-w-[720px]">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            <th className="p-2.5 w-8"></th>
                            <th className="p-2.5">SKU</th>
                            <th className="p-2.5">Item</th>
                            <th className="p-2.5 text-right">Received / Expected</th>
                            <th className="p-2.5 text-right">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.lines.map((l) => {
                            const done = l.status === "received" || l.status === "over";
                            const lineCls =
                              l.status === "received" ? "bg-emerald-100 text-emerald-800" :
                              l.status === "over" ? "bg-amber-100 text-amber-900" :
                              l.status === "partial" ? "bg-blue-100 text-blue-800" :
                              "bg-slate-100 text-slate-700";
                            return (
                              <tr key={l.id} className="border-b border-slate-200 last:border-b-0">
                                <td className="p-2.5">
                                  {done ? <CheckCircle2Icon size={16} className="text-emerald-600" /> : <div className="h-4 w-4 border-2 border-slate-300 rounded-sm" />}
                                </td>
                                <td className="p-2.5 font-mono text-xs text-slate-700">{l.sku}</td>
                                <td className="p-2.5 text-slate-900">{l.item_name}</td>
                                <td className="p-2.5 text-right font-mono">{l.received_quantity} / {l.expected_quantity}</td>
                                <td className="p-2.5 text-right">
                                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${lineCls}`}>{l.status}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );})}
              </div>
            ) : (
              <div className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">
                No expected deliveries scheduled. Use "Schedule New" to pre-load a truckload.
              </div>
            )}
          </div>

          {/* Site deliveries */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Off-schedule</div>
                <div className="mt-1 font-display text-xl font-bold text-slate-900">Ad-hoc Site Deliveries</div>
                <p className="mt-1 text-sm text-slate-600">Deliveries logged without a pre-loaded expected list.</p>
              </div>
              <Button
                onClick={() => setDeliveryOpen(true)}
                variant="outline"
                className="rounded-sm border-slate-300"
                data-testid="deliveries-log-btn"
              >
                <Truck size={14} className="mr-1.5" /> Log Ad-hoc
              </Button>
            </div>
            {deliveries.length ? (
              <div className="divide-y divide-slate-200" data-testid="project-deliveries">
                {deliveries.map((d) => (
                  <div key={d.id} className="p-6" data-testid={`delivery-${d.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-slate-900">{d.reference || "—"}</span>
                          <span className="text-xs text-slate-500 font-mono">{formatDate(d.created_at)}</span>
                        </div>
                        {d.note && <div className="mt-1 text-sm text-slate-600">{d.note}</div>}
                        <div className="mt-1 text-xs text-slate-500">Logged by {d.user_name}</div>
                      </div>
                      <div className="grid grid-cols-4 gap-3 text-xs">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Expected</div>
                          <div className="font-mono font-semibold text-slate-900">{d.totals?.expected ?? 0}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Received</div>
                          <div className="font-mono font-semibold text-slate-900">{d.totals?.received ?? 0}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Short</div>
                          <div className={`font-mono font-semibold ${d.totals?.short ? "text-red-700" : "text-slate-900"}`}>{d.totals?.short ?? 0}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Over</div>
                          <div className={`font-mono font-semibold ${d.totals?.over ? "text-amber-800" : "text-slate-900"}`}>{d.totals?.over ?? 0}</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 border border-slate-200 rounded-sm overflow-hidden">
                      <table className="w-full text-sm min-w-[720px]">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            <th className="p-2.5">SKU</th>
                            <th className="p-2.5">Item</th>
                            <th className="p-2.5 text-right">Expected</th>
                            <th className="p-2.5 text-right">Received</th>
                            <th className="p-2.5 text-right">Variance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.lines.map((l) => {
                            const v = l.variance;
                            const cls = v === 0 ? "bg-emerald-100 text-emerald-800" : v < 0 ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-900";
                            const label = v === 0 ? "OK" : v < 0 ? `${v} short` : `+${v} over`;
                            return (
                              <tr key={l.id} className="border-b border-slate-200 last:border-b-0">
                                <td className="p-2.5 font-mono text-xs text-slate-700">{l.sku}</td>
                                <td className="p-2.5 text-slate-900">
                                  {l.item_name}
                                  {l.unknown_sku && (
                                    <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
                                      Unknown SKU
                                    </span>
                                  )}
                                </td>
                                <td className="p-2.5 text-right font-mono">{l.expected_quantity}</td>
                                <td className="p-2.5 text-right font-mono">{l.received_quantity}</td>
                                <td className="p-2.5 text-right">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold font-mono ${cls}`}>
                                    {label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">
                No site deliveries logged yet. Use "New Delivery" when a truck drops materials at the job.
              </div>
            )}
          </div>
        </div>

        {/* Right rail: settings actions */}
        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Project Settings
            </div>
            <div className="space-y-2">
              <Button
                onClick={() => setEditOpen(true)}
                className="w-full justify-start rounded-sm bg-blue-800 hover:bg-blue-900 text-white"
                data-testid="edit-project-btn"
              >
                <Pencil size={14} className="mr-2" /> Edit Project
              </Button>
              <Button
                onClick={doArchive}
                variant="outline"
                className="w-full justify-start rounded-sm border-slate-300"
                data-testid="archive-project-btn"
              >
                {project.archived ? <ArchiveRestore size={14} className="mr-2" /> : <Archive size={14} className="mr-2" />}
                {project.archived ? "Restore Project" : "Archive Project"}
              </Button>
              {canDelete ? (
                <Button
                  onClick={() => setDeleteOpen(true)}
                  className="w-full justify-start rounded-sm bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-project-btn"
                >
                  <Trash2 size={14} className="mr-2" /> Delete Project
                </Button>
              ) : (
                <Button
                  disabled
                  variant="outline"
                  className="w-full justify-start rounded-sm"
                  data-testid="delete-project-locked-btn"
                >
                  <Lock size={14} className="mr-2" /> Delete (Admin only)
                </Button>
              )}
            </div>
            <div className="mt-4 text-xs text-slate-500 leading-relaxed">
              Deleting a project preserves your physical warehouse quantity. Related receiving
              and transfer records are moved to the audit log — never silently removed.
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Users size={14} /> Team
            </div>
            {(project.assignees_detail?.length || project.crew_size) ? (
              <div className="mt-3 space-y-2">
                {project.crew_size ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Crew size needed</span>
                    <span className="font-mono font-semibold text-slate-900">{project.crew_size}</span>
                  </div>
                ) : null}
                {project.assignees_detail?.length ? (
                  <div className="space-y-1.5">
                    {project.assignees_detail.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`assignee-${a.id}`}>
                        <div className="grid place-items-center h-6 w-6 rounded-sm bg-blue-800 text-white text-[10px] font-bold">
                          {a.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 truncate">{a.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{roleLabel(a.role)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic">No teammates assigned yet. Edit project to assign.</div>
                )}
              </div>
            ) : (
              <div className="mt-3 text-xs text-slate-500 italic">No team assigned yet. Edit project to add crew size and assignees.</div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Stats</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Transactions</span>
                <span className="font-mono font-semibold">{project.transaction_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">On-site item types</span>
                <span className="font-mono font-semibold">{project.onsite_inventory?.length || 0}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        project={project}
        onArchive={doArchive}
        onDelete={doDelete}
      />
      <ProjectFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        project={project}
        onSubmit={submitEdit}
      />
      <TransferInventoryDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        project={project}
        onSuccess={load}
      />
      <ReturnInventoryDialog
        open={returnOpen}
        onOpenChange={setReturnOpen}
        project={project}
        onSuccess={load}
      />
      <SiteDeliveryDialog
        open={deliveryOpen}
        onOpenChange={setDeliveryOpen}
        project={project}
        onSuccess={load}
      />
      <ReceiveDeliveryDialog
        open={receiveDeliveryOpen}
        onOpenChange={setReceiveDeliveryOpen}
        project={project}
        onSuccess={load}
      />
      <NewExpectedDeliveryDialog
        open={newExpectedOpen}
        onOpenChange={setNewExpectedOpen}
        defaultProjectId={project.id}
        onSuccess={load}
      />
      <TaskFormDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        task={editingTask}
        defaultDate={new Date().toISOString().slice(0, 10)}
        defaultProjectId={project.id}
        lockProject
        onSuccess={load}
      />
    </div>
  );
}
